<script lang="ts">
  import { page, router } from "@inertiajs/svelte"
  import { ChevronDown, Languages } from "@lucide/svelte"
  import { _, locale as i18nLocale } from "svelte-i18n"

  import type LanguageMenu from "@/components/language-menu.svelte"
  import type { TriggerAttrs } from "@/components/language-menu.svelte"
  import { regionLocale } from "@/lib/i18n"
  import { setLocale } from "@/lib/locale"
  import {
    baseLanguage,
    regionCountry,
    switchLocalePath,
  } from "@/lib/locale-routes"

  interface Props {
    class?: string
    // `plain` renders a chrome-free trigger (no border, background, or chevron)
    // for quiet placements like the footer; the default is the outline button.
    plain?: boolean
    // Classes for the visible language name, to drop it where space is short.
    // The button's aria-label keeps naming the language either way.
    labelClass?: string
  }

  let { class: className = "", plain = false, labelClass }: Props = $props()

  // Driven by svelte-i18n so the control reflects in-tab switches that
  // don't go through an Inertia visit (e.g. /go keeps the live voice
  // session intact by skipping the redirect-follow remount).
  const current = $derived(
    $i18nLocale ?? (page.props.locale as string | undefined) ?? "en",
  )
  const locales = $derived(
    (page.props.locales as Record<string, string> | undefined) ?? {},
  )
  // `current` can be a region code (e.g. "de-CH" when a geo overlay is
  // active), which never appears in `locales` (base language codes only) —
  // split it down to its base language for the menu's own selected state.
  const currentBase = $derived(baseLanguage(current))
  // Full language name (e.g. "English") rather than the bare locale code.
  const currentLabel = $derived(locales[currentBase] ?? current.toUpperCase())

  // The menu (bits-ui dropdown, popper, scroll lock — ~30 KB with its
  // siblings) is imported on the first pointer-over or click of the trigger,
  // never up front: on the marketing pages every preloaded chunk is fetched
  // by a throttled phone before the hero paints, and most visitors never open
  // this menu. Until it lands the trigger is the same Button on its own; the
  // click that loads the menu also opens it.
  let Menu = $state<typeof LanguageMenu | null>(null)
  let open = $state(false)
  let loading: Promise<void> | undefined
  function load() {
    loading ??= import("@/components/language-menu.svelte").then((m) => {
      Menu = m.default
    })
    return loading
  }
  async function openMenu() {
    await load()
    open = true
  }

  // Public marketing + legal pages have a URL per language, so switching
  // language navigates to the same page in the new language. The in-tab
  // dictionary flip keeps the current geo overlay when the new language has
  // one for the same country ("de-CH" -> switching to tr shows tr, or tr-CH
  // if that overlay existed); the server re-resolves the overlay from
  // CF-IPCountry on the visit anyway — URLs never carry a country. We still
  // persist the base-language choice (cookie + user column) so unprefixed
  // surfaces remember it. Unprefixed pages (sign-in, welcome, the claim
  // landing) have no per-locale URL to visit — setLocale already
  // re-rendered them in-tab, and re-prefixing their path would 404.
  async function pick(next: string) {
    if (next === currentBase) return
    const target = regionLocale(next, regionCountry(current))
    await setLocale(next, target)
    if (!page.props.locale_routed) {
      // Keep a ?locale= carried by the referring page in sync, so a reload
      // doesn't snap back to the pre-switch language.
      const url = new URL(window.location.href)
      if (url.searchParams.has("locale")) {
        url.searchParams.set("locale", next)
        window.history.replaceState(window.history.state, "", url)
      }
      return
    }
    // Inertia scrolls to the top on the visit, which is what we want here: the
    // whole page changes language, so the reader starts it over from the top.
    router.visit(switchLocalePath(window.location, target))
  }
</script>

{#snippet trigger(attrs: TriggerAttrs)}
  <!-- A plain <button>, not ui/button: Button's variant table drags
     tailwind-variants + tailwind-merge (21 KB gz) into every marketing
     page for this one control. These are Button's `sm` box and its
     outline / ghost looks, written out. -->
  <button
    type="button"
    class={`focus-visible:border-ring focus-visible:ring-ring/50 aria-expanded:bg-muted aria-expanded:text-foreground inline-flex h-7 shrink-0 cursor-pointer items-center gap-1 rounded-[min(var(--radius-md),12px)] border text-[0.8rem] font-medium whitespace-nowrap text-[#706f6c] transition-colors outline-none select-none focus-visible:ring-3 dark:text-[#A1A09A] [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0 ${plain ? "hover:text-foreground border-transparent px-1" : "border-border bg-background hover:bg-muted hover:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50 px-2.5"} ${className}`}
    aria-label={`${currentLabel} — ${$_("language_switcher.label")}`}
    {...attrs}
  >
    <Languages aria-hidden="true" />
    <span class={labelClass}>{currentLabel}</span>
    {#if !plain}
      <ChevronDown aria-hidden="true" />
    {/if}
  </button>
{/snippet}

{#if Menu}
  <Menu bind:open {trigger} {locales} {currentBase} onpick={pick} />
{:else}
  {@render trigger({ onclick: openMenu, onpointerenter: load })}
{/if}
